import type { AIPresenceState } from '@/common/components/presence/types';

export type NebulaChoreography = {
  energy: number;
  coherence: number;
  luminosity: number;
  maxFps: number;
  muted: boolean;
};

const CHOREOGRAPHY: Record<AIPresenceState, NebulaChoreography> = {
  // Luminosity is the visible ladder: idle almost off, listening clearly awake,
  // thinking brighter again, speaking level with thinking (voice adds the kick).
  idle: {
    energy: 0.34,
    coherence: 0.35,
    luminosity: 0.18,
    maxFps: 20,
    muted: false,
  },
  listening: {
    energy: 0.48,
    coherence: 0.42,
    luminosity: 0.54,
    maxFps: 24,
    muted: false,
  },
  thinking: {
    energy: 0.78,
    coherence: 0.76,
    luminosity: 0.88,
    maxFps: 45,
    muted: false,
  },
  speaking: {
    energy: 0.78,
    coherence: 0.76,
    luminosity: 0.88,
    maxFps: 45,
    muted: false,
  },
  ready: {
    energy: 0.38,
    coherence: 0.88,
    luminosity: 0.76,
    maxFps: 30,
    muted: false,
  },
  error: {
    energy: 0.12,
    coherence: 0.2,
    luminosity: 0.28,
    maxFps: 30,
    muted: true,
  },
};

export function getNebulaChoreography(state: AIPresenceState): NebulaChoreography {
  return CHOREOGRAPHY[state];
}

/**
 * Motion tempo is a separate axis from locked energy.
 * Idle used to share the "quiet-alive" path with ready and actually moved
 * *faster* than listening (energy 0.48 sits just under the 0.5 work gate).
 */
export type NebulaMotionBand = 'rest' | 'attend' | 'work';

export type NebulaMotionProfile = {
  activeMotion: number;
  centerPeriodFactor: number;
  globalDriftPeriodMs: number;
  globalDriftStrength: number;
  gatherMul: number;
  shapePeriodMul: number;
  internalPeriodMul: number;
  scaleAmplitude: number;
  scaleMin: number;
  scaleMax: number;
  wanderX: number;
  wanderY: number;
  syAmp: number;
  rotationAmp: number;
  emergenceAmp: number;
  phaseAmp: number;
  phaseAmpCos: number;
};

export const NEBULA_MOTION: Record<NebulaMotionBand, NebulaMotionProfile> = {
  rest: {
    activeMotion: 0.24,
    centerPeriodFactor: 1.7,
    globalDriftPeriodMs: 24_000,
    globalDriftStrength: 0.028,
    gatherMul: 0.05,
    shapePeriodMul: 1.3,
    internalPeriodMul: 1.35,
    scaleAmplitude: 0.16,
    scaleMin: 0.94,
    scaleMax: 1.08,
    wanderX: 0.1,
    wanderY: 0.08,
    syAmp: 0.1,
    rotationAmp: 0.18,
    emergenceAmp: 0.12,
    phaseAmp: 0.28,
    phaseAmpCos: 0.2,
  },
  attend: {
    activeMotion: 0.34,
    centerPeriodFactor: 1.4,
    globalDriftPeriodMs: 16_500,
    globalDriftStrength: 0.04,
    gatherMul: 0.08,
    shapePeriodMul: 1.15,
    internalPeriodMul: 1.2,
    scaleAmplitude: 0.18,
    scaleMin: 0.94,
    scaleMax: 1.1,
    wanderX: 0.1,
    wanderY: 0.08,
    syAmp: 0.1,
    rotationAmp: 0.2,
    emergenceAmp: 0.14,
    phaseAmp: 0.3,
    phaseAmpCos: 0.22,
  },
  work: {
    activeMotion: 1,
    centerPeriodFactor: 0.46,
    globalDriftPeriodMs: 5600,
    globalDriftStrength: 0.12,
    gatherMul: 0.28,
    shapePeriodMul: 0.45,
    internalPeriodMul: 0.68,
    scaleAmplitude: 0.44,
    scaleMin: 0.94,
    scaleMax: 1.24,
    wanderX: 0.1,
    wanderY: 0.08,
    syAmp: 0.08,
    rotationAmp: 0.22,
    emergenceAmp: 0.22,
    phaseAmp: 0.4,
    phaseAmpCos: 0.28,
  },
};

export function getNebulaMotionBand(state: AIPresenceState): NebulaMotionBand {
  if (state === 'thinking' || state === 'speaking') return 'work';
  if (state === 'listening') return 'attend';
  return 'rest';
}

export function getNebulaMotionProfile(state: AIPresenceState): NebulaMotionProfile {
  return NEBULA_MOTION[getNebulaMotionBand(state)];
}

/** Extra energy while speaking with live envelope amp (does not alter locked base values). */
export const SPEAKING_AMP_ENERGY_BOOST = 0.28;

/** Multiplier applied to formation contour roughness while speaking with amp. */
export const SPEAKING_AMP_CONTOUR_FACTOR = 0.45;

export function resolveSpeakingEnergy(
  state: AIPresenceState,
  baseEnergy: number,
  speechAmp: number,
): number {
  if (state !== 'speaking' || speechAmp <= 0) return baseEnergy;
  return Math.min(1, baseEnergy + speechAmp * SPEAKING_AMP_ENERGY_BOOST);
}

/**
 * Peak-hold punch accelerates the whole field instead of merely brightening it:
 * each syllable kicks the fog forward, then it coasts back to the thinking pace.
 */
export const SPEAKING_PUNCH_FLOW_ACCEL = 2.4;

export function resolveSpeakingFlowRate(state: AIPresenceState, punch: number): number {
  if (state !== 'speaking' || punch <= 0) return 1;
  return 1 + Math.min(1, punch) * SPEAKING_PUNCH_FLOW_ACCEL;
}

export function resolveSpeakingContourRoughness(
  state: AIPresenceState,
  baseRoughness: number,
  speechAmp: number,
): number {
  if (state !== 'speaking' || speechAmp <= 0) return baseRoughness;
  return baseRoughness * (1 + speechAmp * SPEAKING_AMP_CONTOUR_FACTOR);
}
